import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../wrapper/API';
import { useSuccessBar } from '../context/SuccessBarContext';
import Textfield from '../input/Textfield';
import ActionButton from '../input/ActionButton';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import Icon from '../home/Icon';
import OfflineBanner from '../shell/OfflineBanner';
import PlayerGroupSection from './PlayerGroupSection';
import PlayerRoleSection from './PlayerRoleSection';
import apiErrorText from './apiErrorText';
import '../styles/MainContainer.css';
import './EditPlayerScreen.css';
import { ROUTES } from '../../util/routes';
import { scoreClass } from '../../util/matchScore';

// Schwellwert, ab dem das Anlegen eines neuen Spielers eine Bestätigung braucht.
// Synchron zum Backend-Guard (DuplicateBlockScore in PlayerService.cs).
const DUPLICATE_BLOCK_SCORE = 90;

function EditPlayerScreen({playerId}) {
  const [post, setPost] = useState({});
  const {startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar, successBarState} = useSuccessBar();
  const [formError, setFormError] = useState(null);
  const {openPopup, closePopup} = usePopup();
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  const [nachname, setNachname] = useState("");
  const [vorname, setVorname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const nachnameRef = useRef();
  const vornameRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();
  const [memoValue, setMemoValue] = useState('');

  // Live-Fuzzy-Suche
  const [similarPlayers, setSimilarPlayers] = useState([]);
  const [conflictCandidates, setConflictCandidates] = useState([]); // Popup-Inhalt bei Create-Guard
  const similarDebounceRef = useRef(null);

  // ----- Gruppe & Rolle -----
  // PlayerGroupHistoryResponse { currentGroup, currentRole, memberships[], rolePeriods[] } — loaded once per
  // player and reloaded (only this request) after every group/role change.
  const [history, setHistory] = useState(null);
  const [historyFailed, setHistoryFailed] = useState(false);
  const historyPlayerRef = useRef(null);
  const [userInfo, setUserInfo] = useState(null);    // null = kein Login-Konto; { isRegistrationManager, role }

  const applyPlayer = (p) => {
    setPost(p);
    setNachname(p.lastName ?? "");
    setVorname(p.firstName ?? "");
    setEmail(p.email ?? "");
    setPhone(p.phone ?? "");
    setMemoValue(p.memo ?? "");
    setIsLoaded(true);
  };

  useEffect(() => {
    if (playerId !== '0') {
      api
        .get(`/Player/GetPlayerById?playerId=${playerId}`)
        .then((response) => {
          startSuccessBarSuccess("Spieler geladen.");
          applyPlayer(response.data);
        })
        .catch((error) => {
          closeSuccessBar();
          setFormError(apiErrorText(error, 'Spieler konnte nicht geladen werden.'));
          openPopup("error", 1);
        });
    }
  }, [playerId, closeSuccessBar, openPopup, startSuccessBarSuccess]);

  const loadHistory = useCallback((pid) => {
    historyPlayerRef.current = pid;
    api.get(`/ParticipantGroupMembership/GetForPlayer/${pid}`)
      .then(res => {
        if (historyPlayerRef.current !== pid) return; // navigated to another player meanwhile
        setHistory(res.data ?? null);
        setHistoryFailed(false);
      })
      .catch(() => {
        if (historyPlayerRef.current === pid) setHistoryFailed(true);
      });
  }, []);

  const refreshUserInfo = useCallback((pid) => {
    api.get(`/User/GetForPlayer/${pid}`)
      .then(res => setUserInfo(res.data))
      .catch(() => setUserInfo(null));
  }, []);

  // Anonymized players show no group/role section → nothing to load.
  useEffect(() => {
    if (!isLoaded || !post?.id || post?.isAnonymized) return;
    setHistory(null);
    setHistoryFailed(false);
    loadHistory(post.id);
    refreshUserInfo(post.id);
  }, [isLoaded, post?.id, post?.isAnonymized, loadHistory, refreshUserInfo]);

  // ----- Live-Fuzzy-Suche beim Tippen (debounced) -----
  useEffect(() => {
    const lastClean = (nachname ?? '').trim();
    const firstClean = (vorname ?? '').trim();

    if (lastClean.length < 2 && firstClean.length < 2) {
      setSimilarPlayers([]);
      return;
    }

    if (similarDebounceRef.current) clearTimeout(similarDebounceRef.current);
    similarDebounceRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      if (lastClean) params.append('lastName', lastClean);
      if (firstClean) params.append('firstName', firstClean);
      if (email && email.trim()) params.append('email', email.trim());
      if (isLoaded && post?.id) params.append('excludeId', String(post.id));

      api.get(`/Player/FindSimilar?${params.toString()}`)
        .then((res) => setSimilarPlayers(Array.isArray(res.data) ? res.data : []))
        .catch(() => setSimilarPlayers([]));
    }, 300);

    return () => {
      if (similarDebounceRef.current) clearTimeout(similarDebounceRef.current);
    };
  }, [nachname, vorname, email, isLoaded, post?.id]);

  const loadPlayerById = (id) => {
    if (!id) return;
    navigate(ROUTES.user(id));
  };

  const executeCreate = (confirmDuplicate) => {
    startSuccessBarLoading("Spieler wird erstellt");
    const requestBody = {
      lastName: nachname,
      firstName: vorname,
      email: email,
      phone: phone,
      confirmDuplicate: !!confirmDuplicate
    };

    api.post('/Player/Create', requestBody)
      .then((response) => {
        const newPlayerId = response.data?.id;
        startSuccessBarSuccess("Spieler erstellt");
        if (newPlayerId) navigate(ROUTES.user(newPlayerId));
      })
      .catch(error => {
        closeSuccessBar();
        if (error.response?.status === 409 && Array.isArray(error.response.data?.candidates)) {
          setConflictCandidates(error.response.data.candidates);
          openPopup('confirmcreateduplicate', 1);
          return;
        }
        setFormError(apiErrorText(error, 'Spieler konnte nicht erstellt werden.'));
        openPopup("error", 1);
      });
  };

  const handleAddNewPlayerClick = (event) => {
    event.preventDefault();
    const highMatches = similarPlayers.filter(c => c.score >= DUPLICATE_BLOCK_SCORE);
    if (highMatches.length > 0) {
      setConflictCandidates(highMatches);
      openPopup('confirmcreateduplicate', 1);
      return;
    }
    executeCreate(false);
  };

  const handleConfirmCreateDuplicate = () => {
    closePopup();
    executeCreate(true);
  };

  function reset() {
    setNachname("");
    setVorname("");
    setEmail("");
    setPhone("");
    setIsLoaded(false);
    setPost({});
    historyPlayerRef.current = null;
    setHistory(null);
    setHistoryFailed(false);
    setUserInfo(null);
    setSimilarPlayers([]);
  }

  const handleDiscardClick = () => {
    if (isLoaded) {
      setNachname(post.lastName ?? "");
      setVorname(post.firstName ?? "");
      setEmail(post.email ?? "");
      setPhone(post.phone ?? "");
    } else {
      setNachname("");
      setVorname("");
      setEmail("");
      setPhone("");
    }
  };

  const handleEditPlayerClick = () => {
    startSuccessBarLoading("Änderungen werden übernommen");
    const body = { lastName: nachname, firstName: vorname, email: email, phone: phone };

    api.put(`/Player/EditPlayer?playerId=${post.id}`, body)
      .then(() => {
        startSuccessBarSuccess("Änderungen übernommen");
        setPost(prev => ({ ...prev, lastName: nachname, firstName: vorname, email, phone }));
      })
      .catch((error) => {
        closeSuccessBar();
        setFormError(apiErrorText(error, 'Speichern fehlgeschlagen.'));
        openPopup("error", post.id);
      });
  };

  const handleMemoSave = () => {
    startSuccessBarLoading("Memo wird gespeichert");
    api.put(`/Player/AddMemo?playerId=${post.id}`, JSON.stringify(memoValue), {
      headers: { 'Content-Type': 'application/json' }
    })
      .then((response) => {
        startSuccessBarSuccess("Memo gespeichert");
        setPost(prev => ({ ...prev, memo: response.data.memo }));
        setMemoValue(response.data.memo ?? '');
      })
      .catch(error => {
        closeSuccessBar();
        setFormError(apiErrorText(error, 'Memo konnte nicht gespeichert werden.'));
        openPopup("error", post.id);
      });
  };

  const handleCallClick = () => {
    if (isLoaded) window.location.href = `tel:${phoneRef.current.value}`;
  };

  const handleSendTemplateMailClick = () => {
    if (isLoaded) navigate(ROUTES.mailPlayer(post.id));
  };

  const handleDeletePlayerClick = () => openPopup('warningdeleteplayer', post.id);

  const handleConfirmDeletePlayer = () => {
    startSuccessBarLoading("Spieler wird gelöscht");
    const idToDelete = post.id;
    api.delete(`/Player/Delete?playerId=${idToDelete}`)
      .then(() => {
        startSuccessBarSuccess("Spieler wurde gelöscht");
        reset();
      })
      .catch((error) => {
        closeSuccessBar();
        setFormError(apiErrorText(error, 'Löschen fehlgeschlagen.'));
        openPopup("error", idToDelete);
      });
    closePopup();
  };

  const handleAnonymizePlayerClick = () => openPopup('warninganonymizeplayer', post.id);

  const handleConfirmAnonymizePlayer = () => {
    startSuccessBarLoading("Spieler wird anonymisiert");
    const idToAnonymize = post.id;
    api.post(`/Player/Anonymize?playerId=${idToAnonymize}`)
      .then(() => {
        startSuccessBarSuccess("Spieler wurde anonymisiert");
        reset();
      })
      .catch((error) => {
        closeSuccessBar();
        setFormError(apiErrorText(error, 'Anonymisieren fehlgeschlagen.'));
        openPopup("error", idToAnonymize);
      });
    closePopup();
  };

  // ----- Gruppe & Rolle -----
  const handleGroupHistoryChanged = () => {
    if (post?.id) loadHistory(post.id);
  };

  // Step mails to groups (process "Member-Mail" etc.) only go to players with this flag.
  const handleGroupMailsChange = (e) => {
    const newValue = e.target.checked;
    setPost(prev => ({ ...prev, memberMailEnabled: newValue }));
    api.put(`/Player/EditPlayer?playerId=${post.id}`, { memberMailEnabled: newValue })
      .then(res => setPost(prev => ({ ...prev, memberMailEnabled: res.data?.memberMailEnabled ?? newValue })))
      .catch(() => setPost(prev => ({ ...prev, memberMailEnabled: !newValue })));
  };

  const createMode = !isLoaded;
  const isAnonymized = !!post.isAnonymized;

  const similarLabel = createMode
    ? (similarPlayers.length === 1 ? 'Ähnlicher Spieler gefunden:' : `Ähnliche Spieler gefunden (${similarPlayers.length}):`)
    : (similarPlayers.length === 1 ? 'Achtung, ähnlicher Spieler existiert:' : `Achtung, ähnliche Spieler existieren (${similarPlayers.length}):`);

  return (
    <React.Fragment>
      <main className={"wideMain"}>
        <OfflineBanner />
        <div className={"innerContainer"}>
          {isAnonymized && (
            <div className={"editPlayerAnonymizedBanner"}>
              <Icon className={"editPlayerAnonymizedBannerIcon"} icon={"visibility_off"} />
              <span>Dieser Spieler wurde anonymisiert (DSGVO). Bearbeiten ist nicht mehr möglich — Spieltagsstatistik und Gruppenverlauf bleiben für die Buchhaltung erhalten.</span>
            </div>
          )}
          <div className={"generalInformationContainer"}>
            <label className={"innerContainerLabel"}>Grundlagen</label>
            <Textfield ref={vornameRef} icon={"face"} fill type={"name"} name={"firstname"} label={"Vorname"} placeholder={"Max"} onChange={(e) => setVorname(e.target.value)} value={vorname} disabled={isAnonymized} />
            <Textfield ref={nachnameRef} icon={"id_card"} type={"name"} name={"lastname"} label={"Nachname"} placeholder={"Mustermann"} onChange={(e) => setNachname(e.target.value)} value={nachname} disabled={isAnonymized} />
            <Textfield ref={emailRef} icon={"alternate_email"} type={"email"} name={"email"} label={"E-Mail"} placeholder={"max.mustermann@test.de"} onChange={(e) => setEmail(e.target.value)} value={email} disabled={isAnonymized} />
            <Textfield ref={phoneRef} icon={"call"} type={"phone"} fill name={"phone"} label={"Telefonnummer"} placeholder={"0711 1234567"} onChange={(e) => setPhone(e.target.value)} value={phone} disabled={isAnonymized} />

            {similarPlayers.length > 0 && (
              <div className={"editPlayerSimilar"}>
                <span className={`editPlayerSimilarLabel ${createMode ? '' : 'edit'}`}>
                  <Icon icon={createMode ? 'person_search' : 'warning'} />
                  {similarLabel}
                </span>
                {similarPlayers.map((m) => {
                  const sc = scoreClass(m.score);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={`editPlayerSimilarMatch ${sc}`}
                      onClick={() => loadPlayerById(m.id)}
                      title={`${m.firstName} ${m.lastName}\nScore: ${m.score}${m.email ? `\nEmail: ${m.email}` : ''}${m.phone ? `\nTel: ${m.phone}` : ''}`}
                    >
                      <span className={"editPlayerSimilarName"}>
                        {m.firstName} {m.lastName}
                        {m.namesSwapped && (
                          <span className={"swapCandidateHint"}>
                            <Icon className={"swapCandidateHintIcon"} icon={'swap_horiz'} />
                            Namen vertauscht?
                          </span>
                        )}
                      </span>
                      <span className={`editPlayerSimilarScore ${sc}`}>{m.score}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className={"sideContainer"}>
            <label className={"innerContainerLabel"}>Allgemeine Spielermemo</label>
            <textarea
              className={"memoInput"}
              value={memoValue}
              onChange={e => setMemoValue(e.target.value)}
              maxLength={500}
              placeholder='Spielermemo...'
              disabled={!isLoaded || successBarState === 'loading' || isAnonymized}
            />
            {isLoaded && !isAnonymized && memoValue !== (post.memo ?? '') && successBarState !== 'loading' &&
              <div className={"editPlayerSectionActionBar"}>
                <ActionButton icon="check" label="Memo speichern" type={3} onClick={handleMemoSave} />
              </div>
            }
          </div>
          <div className={"innerContainerActionBar"}>
            {isLoaded && successBarState !== 'loading' && isAnonymized && (
              <ActionButton icon="delete_sweep" label="Löschen" type={1} onClick={handleDeletePlayerClick} />
            )}
            {isLoaded && successBarState !== 'loading' && !isAnonymized && (() => {
              const hasChanges = nachname !== post.lastName || vorname !== post.firstName ||
                email !== post.email || phone !== post.phone;
              return (
                <React.Fragment>
                  {hasChanges && <ActionButton icon="check" label="Speichern" type={2} onClick={handleEditPlayerClick} />}
                  <ActionButton icon="visibility_off" label="Anonymisieren" type={1} onClick={handleAnonymizePlayerClick} />
                  <ActionButton icon="delete_sweep" label="Löschen" type={1} onClick={handleDeletePlayerClick} />
                  {hasChanges && <ActionButton icon="replay" label="Verwerfen" onClick={handleDiscardClick} />}
                  <ActionButton icon="call" label="Anrufen" onClick={handleCallClick} />
                  {post.email && <ActionButton icon="email" label="Mail senden" onClick={handleSendTemplateMailClick} />}
                </React.Fragment>
              );
            })()}
            {!isLoaded && successBarState !== 'loading' && (
              <React.Fragment>
                {(nachname || vorname || email || phone) && <ActionButton icon="replay" label="Verwerfen" onClick={handleDiscardClick} />}
                <ActionButton icon="person_add" label="Spieler anlegen" type={3} onClick={handleAddNewPlayerClick} />
              </React.Fragment>
            )}
          </div>
        </div>

        {isLoaded && !isAnonymized && (
          <div className={"innerContainer editPlayerGroupRoleContainer"}>
            <PlayerGroupSection
              player={post}
              history={history}
              historyFailed={historyFailed}
              onChanged={handleGroupHistoryChanged}
              groupMailsEnabled={!!post.memberMailEnabled}
              onGroupMailsChange={handleGroupMailsChange}
            />
            <PlayerRoleSection
              player={post}
              history={history}
              historyFailed={historyFailed}
              userInfo={userInfo}
              onUserInfoChange={setUserInfo}
              onChanged={handleGroupHistoryChanged}
            />
          </div>
        )}

        {createMode && (
          <div className={"innerContainer editPlayerGroupRoleContainer"}>
            <div className={"editPlayerGroupRolePlaceholder"}>
              <label className={"innerContainerLabel"}>Gruppe &amp; Rolle</label>
              <p className={"editPlayerGroupRoleHint"}>Lege den Spieler zuerst an — danach kannst du ihm eine Gruppe zuweisen und eine Orga-/Admin-Rolle setzen.</p>
            </div>
          </div>
        )}
      </main>
      <Popup name='error' confirmText='Ok' headerText='Fehler' type="error">
        <p>{formError}</p>
      </Popup>
      <Popup name='warningdeleteplayer' confirmText='Bestätigen' headerText='Warnung'
        type="warning" onConfirm={handleConfirmDeletePlayer} withCancel
      >
        <p>Möchtest du <strong>{`${post.lastName ?? ''}, ${post.firstName ?? ''}`}</strong> wirklich dauerhaft löschen?</p>
        <p className={"formFieldHint"}>Die Teilnahmen an vergangenen Spieltagen bleiben als historische Einträge erhalten.</p>
      </Popup>
      <Popup name='warninganonymizeplayer' confirmText='Anonymisieren' headerText='Spieler anonymisieren'
        type="warning" onConfirm={handleConfirmAnonymizePlayer} withCancel
      >
        <p>Möchtest du <strong>{`${post.lastName ?? ''}, ${post.firstName ?? ''}`}</strong> wirklich anonymisieren?</p>
        <p className={"formFieldHint"}>Name, E-Mail, Telefon und Memos werden entfernt. Spieltagsstatistik und Gruppenverlauf bleiben (ohne Klarnamen) erhalten.</p>
      </Popup>
      <Popup name='confirmcreateduplicate' headerText='Ähnlicher Spieler existiert bereits'
        type="warning" confirmText='Trotzdem neu anlegen'
        onConfirm={handleConfirmCreateDuplicate} withCancel
      >
        <p>Es gibt bereits {conflictCandidates.length === 1 ? 'einen Spieler' : `${conflictCandidates.length} Spieler`} mit sehr ähnlichen Daten. Klicke einen Eintrag an um diesen zu öffnen statt neu anzulegen:</p>
        <div className={"editPlayerDuplicateList"}>
          {conflictCandidates.map((c) => {
            const sc = scoreClass(c.score);
            return (
              <button
                key={c.id}
                type="button"
                className={`editPlayerDuplicateItem ${sc}`}
                onClick={() => { closePopup(); loadPlayerById(c.id); }}
              >
                <span className={`editPlayerDuplicateScore ${sc}`}>{c.score}</span>
                <span className={"editPlayerDuplicateName"}>{c.firstName} {c.lastName}</span>
                <span className={"editPlayerDuplicateContact"}>{c.email || '–'}{c.phone ? ` · ${c.phone}` : ''}</span>
              </button>
            );
          })}
        </div>
      </Popup>
    </React.Fragment>
  );
}

export default EditPlayerScreen;
